import { useCallback, useRef } from "react";
import { useGlobalState } from "../../../contexts/GlobalContext";
import { authHelper } from "../../../utils/helpers";
import { authenticateSSOUser, fetchSessionProxy, fetchUpdateUserMeataData } from "../../../utils/helpers/api";
import { fetchGetOauthToken } from "../../../utils/helpers/auth0";
import { jwtDecode } from "jwt-decode";

export const useLayoutAuthentication = () => {
  const { globalState, setGlobalState, refreshAccessToken, fetchAndSetUserInfo } = useGlobalState();
  const calledAuthenticateInitialUser = useRef(false);
  const calledAfterGetAuthorizationCodeHandler = useRef(false);
  const handleAfterGetAuthorizationCode = useCallback(async (code: string, callback?: (params: {
    sessionId: string;
    userStatus: number;
  }) => any) => {
    if (!code || calledAfterGetAuthorizationCodeHandler.current) {
      return;
    }
    const res = await fetchGetOauthToken(code);

    const { access_token, expires_in, refresh_token } = res;
    authHelper.setAccessToken(access_token, expires_in);
    authHelper.setValueToCookie("_rftk", refresh_token, "31536000");
    authHelper.setValueToCookie("_exp_in", expires_in, "31536000");


    const result = await fetchSessionProxy({
      access_token,
      expires_in,
      refresh_token,
      external: true,
    });
    const user = await fetchAndSetUserInfo();
    const sessionId = result?.data?.session_id ?? "";
    const { user_metadata } = user ?? {};
    const transitionSource = localStorage.getItem("transition_source");
    if (transitionSource && !user_metadata?.transition_source && (user_metadata.user_status === 1 || user_metadata.user_status === 3)) {
      const dataUpdate: IUserMetaDataApi = user_metadata ?? {};
      dataUpdate.transition_source = transitionSource;
      const decode = jwtDecode(access_token);
      await handleUpdateTransitionSource(decode.sub ?? "", dataUpdate);
    }

    if (callback) {
      callback({
        sessionId,
        userStatus: user?.user_metadata?.user_status ?? 0,
      });
    }
    calledAfterGetAuthorizationCodeHandler.current = true;
  }, []);

  const handleUpdateTransitionSource = async (
    user_id: string,
    data: IUserMetaDataApi
  ) => {
    try {
      const res = await fetchUpdateUserMeataData(user_id ?? "", data);
      if (res) {
        localStorage.removeItem("transition_source");
      }
    } catch (err) {
      return err;
    }
  };

  const authenticateInitialUser = useCallback(async () => {
    if (!globalState.user || calledAuthenticateInitialUser.current) {
      return;
    }
    const raw = localStorage.getItem("transition_source");
    const isLoginGoogle = localStorage.getItem("isGoogleLogin");
    let transitionSource = null;
    if (raw) {
      try {
        const parsed = JSON.parse(raw);
        if (parsed && typeof parsed === "object" && "value" in parsed) {
          transitionSource = parsed.value;
        } else {
          transitionSource = raw;
        }
      } catch {
        transitionSource = raw;
      }
    }
    const { user_id, user_metadata } = globalState.user;
    const hasSource = Boolean(user_metadata?.transition_source);
    const updatedMetadata = {
      ...user_metadata,
      authenticated: 1,
      user_status: 1,
      ...(transitionSource &&
        isLoginGoogle &&
        !hasSource && {
        transition_source: transitionSource,
      }),
    };
    if (transitionSource && isLoginGoogle) {
      localStorage.removeItem("transition_source");
    }
    try {
      await fetchUpdateUserMeataData(user_id ?? "", updatedMetadata);
      await authenticateSSOUser(
        globalState.user.email,
        globalState.user.user_id
      );
      await refreshAccessToken();
      calledAuthenticateInitialUser.current = true;
    } catch (err) {
      throw err;
    }
  }, [globalState, setGlobalState]);

  const updateTransitionSource = useCallback(async (transitionSource: string) => {
    const dataUpdate: IUserMetaDataApi = {};
    dataUpdate.transition_source = transitionSource;

    await fetchUpdateUserMeataData(
      globalState?.user?.user_id ?? "",
      dataUpdate
    );
  }, [globalState?.user?.user_id]);


  return {
    handleAfterGetAuthorizationCode,
    authenticateInitialUser,
    updateTransitionSource,
  }
}
